import React, {useMemo} from 'react';
import {PathUtility} from 'src/shared/constants/PathBuilder';

import {Menu} from '@mantine/core';
import {Group} from 'src/design/atoms/Group';
import type {MRT_ColumnDef} from 'mantine-react-table';
import type {
  BulletinSimpleResponse} from 'src/api/ProactApiClient';
import {
  ModuleEnum,
  PermissionOperationEnum,
} from 'src/api/ProactApiClient';
import {CreateButton} from 'src/design/molecules/CreateButton';
import {Pill} from 'src/design/atoms/Pill';
import {ActiveInactivePills} from 'src/shared/constants/PillConstants';
import {ActiveStateDataOptions} from 'src/shared/constants/columnFilterData';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {PermissionGuard} from 'src/app/security/authorization/PermissionGuard';
import {useGetBulletinListV2Query} from 'src/redux/queries/bulletins';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

const ListBulletins: React.FC = () => {
  const navigate = useAppNavigate();

  const columns = useMemo<MRT_ColumnDef<BulletinSimpleResponse>[]>(
    () => [
      {
        accessorKey: 'internalIdentifier',
        header: 'Identifier',
      },
      {
        accessorKey: 'title',
        header: 'Title',
      },
      {
        accessorKey: 'active',
        header: 'Active',
        enableColumnFilterModes: false,
        filterVariant: 'multi-select',
        mantineFilterSelectProps: {
          data: ActiveStateDataOptions,
        },
        Cell: ({cell}) => (
          <Pill {...ActiveInactivePills(cell.getValue<boolean>())} />
        ),
      },
    ],
    []
  );

  const handleAddNew = () => navigate(PathUtility.BulletinCreate('content'));

  const handleNavigateToForm = (id: string | number) =>
    navigate(PathUtility.BulletinView(id, 'content'));

  const tableState = useTableState({
    identifier: 'bulletins',
    columns: columns,
    defaultDensity: 'xs',
    defaultPagination: {
      pageIndex: 0,
      pageSize: 20,
    },
    defaultSorting: [{id: 'internalIdentifier', desc: true}],
    defaultShowColumnFilters: true,
  });

  const {start, length, filters, filterFns, sorting} = tableState;

  const {data, isLoading, isError, isFetching, refetch} =
    useGetBulletinListV2Query(
      {
        start: start,
        length: length,
        filters: filters,
        filterFns: filterFns,
        sorting: sorting,
      },
      {refetchOnMountOrArgChange: true}
    );

  const rows = data?.data ?? [];
  const totalCount = data?.totalRecords ?? 0;

  const {table} = useTable({
    ...tableState,
    tableOptions: {
      manualSorting: true,
      manualFiltering: true,
      manualPagination: true,
      columns: columns,
      data: rows,
      renderRowActionMenuItems: ({row}) => (
        <>
          <Menu.Item onClick={() => handleNavigateToForm(row.original.id)}>
            View
          </Menu.Item>
        </>
      ),
      renderTopToolbarCustomActions: () => (
        <Group justify="end" gap={3}>
          <PermissionGuard
            module={ModuleEnum.Bulletins}
            operation={PermissionOperationEnum.Create}>
            <CreateButton onClick={handleAddNew} />
          </PermissionGuard>
        </Group>
      ),
    },
    totalCount,
    isError,
    isLoading,
    isFetching,
    onRefresh: refetch,
  });

  return <>{table}</>;
};

export default ListBulletins;
